/**
 * Coverage types - Re-exported from generated API types
 * Following the pattern from types/shifts.ts
 */

import { shifts_GroupCoverageDTO, shifts_SlotChildPresenceDTO, shifts_SlotDTO } from '@/api';

// Re-export generated types with friendly names
// Note: CoverageResponse is now defined locally in useShiftCoverage.ts for backward
// compatibility, as the API now returns a unified CoverageRangeResponse format.
export type {
  shifts_CoverageRangeResponse as CoverageRangeResponse,
  shifts_GroupCoverageDTO as GroupCoverage,
  shifts_DailyCoverageDTO as DailyCoverage,
  shifts_UnderstaffedResponse as UnderstaffedResponse,
  shifts_UnderstaffedGroupDTO as UnderstaffedGroup,
} from '@/api';

// Coverage status constants - all possible statuses returned by the API.
// Aliased onto the generated enum rather than re-declared as a const object:
// the hand-written copy had identical members but was a distinct (structural)
// type, so every `CoverageStatus.X` written into a `GroupCoverage` fixture was
// rejected against the nominal generated enum. Same runtime values, same member
// names — call sites are unchanged.
export const CoverageStatus = shifts_GroupCoverageDTO.status;

export type CoverageStatusType = shifts_GroupCoverageDTO.status;

/**
 * Coverage statuses from the most to the least urgent: understaffed groups
 * lead, groups with nothing to staff come last. The live endpoint's own enum
 * carries the same values, so the helpers take a plain string.
 */
export const COVERAGE_SEVERITY: readonly CoverageStatusType[] = [
  CoverageStatus.CRITICAL,
  CoverageStatus.WARNING,
  CoverageStatus.SUFFICIENT,
  CoverageStatus.NO_CHILDREN,
  CoverageStatus.NOT_CONFIGURED,
];

/** Sort comparator, the more urgent status first; an unknown status sorts last. */
export function compareCoverageSeverity(a: string | undefined, b: string | undefined): number {
  const rank = (status: string | undefined) => {
    const index = COVERAGE_SEVERITY.indexOf(status as CoverageStatusType);
    return index === -1 ? COVERAGE_SEVERITY.length : index;
  };
  return rank(a) - rank(b);
}

// Per-15-minute slot coverage (GET /api/v1/shifts/coverage/slots, #1579).
export type {
  shifts_SlotCoverageResponse as SlotCoverage,
  shifts_GroupSlotCoverageDTO as SlotCoverageGroup,
  shifts_SlotDTO as SlotCoverageSlot,
  shifts_SlotTimeRunDTO as SlotTimeRun,
  shifts_SlotChildPresenceDTO as SlotChildPresence,
} from '@/api';

export const SlotCoverageStatus = shifts_SlotDTO.status;

export type SlotCoverageStatusType = shifts_SlotDTO.status;

// Where a forecast arrival or departure time comes from. Arrival and departure share
// the same members.
export const PresenceSource = shifts_SlotChildPresenceDTO.arrival_source;

// The generated client emits a separate, identical enum for departure_source.
export const DeparturePresenceSource = shifts_SlotChildPresenceDTO.departure_source;

export type PresenceSourceType =
  | shifts_SlotChildPresenceDTO.arrival_source
  | shifts_SlotChildPresenceDTO.departure_source;
